import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Card,
  Input,
  Button,
  Space,
  Typography,
  Skeleton,
  Alert,
  message,
  Switch,
} from 'antd';
import {
  SearchOutlined,
  ClearOutlined,
  LinkOutlined,
  TrophyOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import styled, { keyframes } from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../Modules/Presentation/hooks';
import {
  selectUrlRankLoading,
  selectUrlRankPolling,
  selectUrlRankJobId,
  selectUrlRankSearchRange,
  selectUrlRankResult,
  selectUrlRankError,
} from '../../Modules/UrlRank/Presentation/selectors';
import { startUrlRankCheck, pollUrlRankJob } from '../../Modules/UrlRank/Presentation/actions';
import { clearUrlRankState } from '../../Modules/UrlRank/Presentation/reducers';
import {
  UrlRankRequest,
  UrlRankResult,
  URL_RANK_POLL_INTERVAL_MS,
  isTerminalUrlRankStatus,
} from '../../Modules/UrlRank/Model/UrlRank';

const { Title, Text } = Typography;

const PageContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const HeaderCard = styled(Card)`
  text-align: center;
  margin-bottom: 16px;
  border-radius: 8px;
`;

const InputCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 8px;
`;

const ResultCard = styled(Card)`
  border-radius: 8px;
`;

const FormField = styled.div`
  margin-bottom: 16px;

  .field-label {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 6px;
    display: block;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const InfoRow = styled.div`
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  word-break: break-all;
`;

const RankDisplay = styled.div`
  text-align: center;
  padding: 24px 16px;
`;

const RankNumber = styled.div`
  font-size: 48px;
  font-weight: 700;
  color: #b03052;
  line-height: 1.1;
`;

const RefreshRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.85; }
  50% { transform: scale(1.08); opacity: 1; }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const CheckingPanel = styled.div`
  text-align: center;
  padding: 8px 4px 4px;
`;

const CheckingIconWrap = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f7d6de 0%, #f3b8c7 100%);
  color: #b03052;
  font-size: 28px;
  animation: ${pulse} 1.6s ease-in-out infinite;
`;

const CheckingTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #3d0301;
  margin-bottom: 6px;
`;

const CheckingHint = styled.div`
  font-size: 14px;
  color: #666;
  min-height: 22px;
  margin-bottom: 8px;
  animation: ${bounce} 2.4s ease-in-out infinite;
`;

const ElapsedText = styled.div`
  font-size: 13px;
  color: #999;
  margin-bottom: 16px;
`;

const SoftProgressTrack = styled.div`
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  background: #f5e6ea;
  margin-bottom: 8px;
`;

const SoftProgressFill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${({ $percent }) => `${$percent}%`};
  border-radius: 999px;
  background: linear-gradient(90deg, #d76c82, #b03052, #d76c82);
  background-size: 200% 100%;
  animation: ${shimmer} 1.8s linear infinite;
  transition: width 0.8s ease;
`;

const CHECKING_HINTS = [
  '상품을 찾고 있어요...',
  '순위를 하나씩 확인하고 있어요...',
  '조금 더 기다려 주세요...',
  '거의 다 왔어요. 조금만 더요...',
  '광고는 빼고 확인하고 있어요...',
];

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m <= 0) return `${s}초 지났어요`;
  return `${m}분 ${s.toString().padStart(2, '0')}초 지났어요`;
}

/** 오래 걸려도 끝없이 천천히 올라가되 95%에서 멈춤 */
function progressFromElapsed(seconds: number): number {
  return Math.min(95, Math.round(12 + (83 * (1 - Math.exp(-seconds / 70)))));
}

function getResultSummary(result: UrlRankResult, searchRange: number | null): {
  title: string;
  description: string;
  tone: 'success' | 'warning' | 'info';
} {
  const range = result.search_range ?? searchRange ?? 480;

  if (result.rank_check_status === 'partial') {
    return {
      title: '확인 못함',
      description: '이번에 순위를 제대로 확인하지 못했습니다. 다시 확인해 주세요.',
      tone: 'warning',
    };
  }

  if (result.rank == null) {
    return {
      title: `${range}위 안에 없음`,
      description: `지금 확인한 범위(${range}위) 안에서는 이 상품을 찾지 못했습니다.`,
      tone: 'info',
    };
  }

  if (result.rank_check_status === 'stored_complete') {
    return {
      title: `${result.rank}위`,
      description: '오늘 이미 확인된 순위입니다.',
      tone: 'success',
    };
  }

  return {
    title: `${result.rank}위`,
    description: '방금 확인한 순위입니다. (광고 제외)',
    tone: 'success',
  };
}

const UrlRankPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectUrlRankLoading);
  const polling = useAppSelector(selectUrlRankPolling);
  const jobId = useAppSelector(selectUrlRankJobId);
  const searchRange = useAppSelector(selectUrlRankSearchRange);
  const result = useAppSelector(selectUrlRankResult);
  const error = useAppSelector(selectUrlRankError);

  const [formData, setFormData] = useState<UrlRankRequest>({
    keyword: '',
    product_url: '',
    force_refresh: false,
  });
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [hintIndex, setHintIndex] = useState(0);

  const pollStartedAtRef = useRef<number | null>(null);
  const busy = loading || polling;
  const isChecking = polling && !result && !error;

  useEffect(() => {
    if (!jobId || result || error) return;

    let cancelled = false;
    pollStartedAtRef.current = Date.now();
    setElapsedSeconds(0);
    setHintIndex(0);

    const tick = async () => {
      if (cancelled) return;

      try {
        const status = await dispatch(pollUrlRankJob(jobId)).unwrap();
        if (cancelled) return;

        if (isTerminalUrlRankStatus(status.rank_check_status)) {
          if (status.rank_check_status === 'complete' && status.rank == null) {
            message.info(`${status.search_range ?? searchRange ?? 480}위 안에서는 찾지 못했습니다.`);
          } else if (status.rank != null) {
            message.success(`현재 순위: ${status.rank}위`);
          } else if (status.rank_check_status === 'partial') {
            message.warning('순위를 제대로 확인하지 못했습니다. 다시 시도해 주세요.');
          }
          return;
        }

        window.setTimeout(tick, URL_RANK_POLL_INTERVAL_MS);
      } catch {
        // error handled in store
      }
    };

    void tick();

    return () => {
      cancelled = true;
    };
  }, [dispatch, jobId, result, error, searchRange]);

  useEffect(() => {
    if (!isChecking) return;

    const timer = window.setInterval(() => {
      const startedAt = pollStartedAtRef.current;
      if (!startedAt) return;
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [isChecking]);

  useEffect(() => {
    if (!isChecking) return;

    const timer = window.setInterval(() => {
      setHintIndex((prev) => (prev + 1) % CHECKING_HINTS.length);
    }, 2800);

    return () => window.clearInterval(timer);
  }, [isChecking]);

  const softProgress = useMemo(
    () => progressFromElapsed(elapsedSeconds),
    [elapsedSeconds],
  );

  const handleSubmit = useCallback(async () => {
    if (!formData.keyword.trim() || !formData.product_url.trim()) {
      message.error('키워드와 상품 링크를 모두 입력해 주세요.');
      return;
    }

    try {
      dispatch(clearUrlRankState());
      const started = await dispatch(startUrlRankCheck(formData)).unwrap();

      if (started.kind === 'job') {
        message.info('순위를 확인하고 있습니다. 잠시만 기다려 주세요.');
        return;
      }

      const data = started.data;
      if (data.rank == null) {
        message.info(`${data.search_range ?? 480}위 안에서는 찾지 못했습니다.`);
      } else {
        message.success(`현재 순위: ${data.rank}위`);
      }
    } catch {
      // error state is handled in the store
    }
  }, [dispatch, formData]);

  const handleClear = useCallback(() => {
    dispatch(clearUrlRankState());
    setFormData({ keyword: '', product_url: '', force_refresh: false });
    message.info('입력 내용과 결과를 지웠습니다.');
  }, [dispatch]);

  const handleInputChange = useCallback((field: keyof UrlRankRequest, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const canSubmit = Boolean(formData.keyword.trim() && formData.product_url.trim());
  const summary = result ? getResultSummary(result, searchRange) : null;

  return (
    <PageContainer>
      <HeaderCard>
        <Title level={2} style={{ margin: 0 }}>링크 순위 확인</Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          키워드와 상품 링크로 지금 몇 위인지 확인합니다
        </Text>
      </HeaderCard>

      <InputCard title="확인하기">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <FormField>
            <label className="field-label">키워드</label>
            <Input
              size="large"
              placeholder="예: 무선청소기"
              value={formData.keyword}
              onChange={(e) => handleInputChange('keyword', e.target.value)}
              onPressEnter={() => {
                if (canSubmit && !busy) void handleSubmit();
              }}
              disabled={busy}
            />
          </FormField>

          <FormField>
            <label className="field-label">상품 링크</label>
            <Input
              size="large"
              prefix={<LinkOutlined />}
              placeholder="스마트스토어 상품 주소를 붙여넣어 주세요"
              value={formData.product_url}
              onChange={(e) => handleInputChange('product_url', e.target.value)}
              onPressEnter={() => {
                if (canSubmit && !busy) void handleSubmit();
              }}
              disabled={busy}
            />
          </FormField>

          <RefreshRow>
            <div>
              <Text strong>새로 확인하기</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                켜면 오늘 확인했던 결과를 쓰지 않고 다시 확인합니다
              </Text>
            </div>
            <Switch
              checked={!!formData.force_refresh}
              onChange={(checked) => handleInputChange('force_refresh', checked)}
              checkedChildren={<ReloadOutlined />}
              disabled={busy}
            />
          </RefreshRow>

          <ButtonContainer>
            <Button
              type="primary"
              size="large"
              icon={<SearchOutlined />}
              onClick={() => void handleSubmit()}
              loading={busy}
              disabled={!canSubmit}
            >
              {formData.force_refresh ? '새로 확인' : '순위 확인'}
            </Button>
            <Button
              size="large"
              icon={<ClearOutlined />}
              onClick={handleClear}
              disabled={busy}
            >
              지우기
            </Button>
          </ButtonContainer>
        </Space>
      </InputCard>

      {loading && (
        <ResultCard>
          <Skeleton active paragraph={{ rows: 3 }} />
          <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 8 }}>
            확인 중입니다...
          </Text>
        </ResultCard>
      )}

      {isChecking && (
        <ResultCard title="확인 중">
          <CheckingPanel>
            <CheckingIconWrap>
              <SearchOutlined />
            </CheckingIconWrap>
            <CheckingTitle>순위를 확인하고 있습니다</CheckingTitle>
            <CheckingHint>{CHECKING_HINTS[hintIndex]}</CheckingHint>
            <ElapsedText>
              최대 {searchRange ?? 480}위까지 찾는 중 · {formatElapsed(elapsedSeconds)}
            </ElapsedText>
            <SoftProgressTrack>
              <SoftProgressFill $percent={softProgress} />
            </SoftProgressTrack>
            <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 13 }}>
              조금 오래 걸릴 수 있어요. 끝나면 바로 결과를 보여드릴게요.
            </Text>
          </CheckingPanel>
        </ResultCard>
      )}

      {error && !loading && (
        <ResultCard>
          <Alert
            message="확인하지 못했습니다"
            description="잠시 후 다시 시도해 주세요."
            type="error"
            showIcon
          />
        </ResultCard>
      )}

      {result && summary && !loading && (
        <ResultCard title="결과">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <RankDisplay>
              <TrophyOutlined style={{ fontSize: 28, color: '#faad14', marginBottom: 8 }} />
              <RankNumber
                style={
                  result.rank == null
                    ? { fontSize: 28, color: summary.tone === 'warning' ? '#d48806' : '#666' }
                    : undefined
                }
              >
                {summary.title}
              </RankNumber>
              <Text type="secondary">{summary.description}</Text>
            </RankDisplay>

            <InfoRow>
              <Text strong>키워드:</Text>
              <Text>{('keyword' in result && result.keyword) || formData.keyword}</Text>
            </InfoRow>
            <InfoRow>
              <Text strong>상품 링크:</Text>
              <Text>
                {('product_url' in result && result.product_url) ? (
                  <a href={result.product_url} target="_blank" rel="noopener noreferrer">
                    {result.product_url}
                  </a>
                ) : (
                  formData.product_url
                )}
              </Text>
            </InfoRow>

            {result.rank_check_status === 'stored_complete' && !formData.force_refresh && (
              <Alert
                type="info"
                showIcon
                message="오늘 이미 확인된 결과입니다"
                description="다시 보려면 위쪽 '새로 확인하기'를 켠 뒤 확인해 주세요."
              />
            )}
          </Space>
        </ResultCard>
      )}
    </PageContainer>
  );
};

export default UrlRankPage;
